import React, {
	useContext,
	useEffect, useMemo, useRef, useState,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import TextInput from '../general-components/text-input';
import SelectInput from '../general-components/select-dropdown';
import TooltipWrapper, { TTPosition } from '../general-components/tooltips/tooltip-wrapper';
import SIPConferenceBackground from '../../images/sip-conference-background.jpg';
import ZoomLogo from '../../images/zoom-logo.svg';
import DefaultSafetyImage from '../../images/default-safety-image.png';
import MicrosoftTeamsLogo from '../../images/microsoft-teams-logo.svg';
import WebexLogo from '../../images/webex-logo.svg';
import BrandliveLogo from '../../images/brandlivelogo.svg';
import { IMG_TYPES } from '../../types/constants';
import { GreenroomState as GS } from '../../stores/types/types';
import CallSipUser from '../../connection/sip';
import { UploadImage } from '../../connection/upload-image';
import ConferenceRoomEntry from './conferencing/conference-room-entry';
import SuccessSplash from './conferencing/success-splash';
import { setCompanionMode } from '../../stores/actions/session';
import * as Alerts from '../general-components/alert-service';

import ConferenceViewConfig from './conferencing/conference-view-config';
import { authorizeLimitedInput } from '../../connection/limited-device-auth';
import { SocketContext } from '../../contexts/socket';
import { LimitedInputConfig, LimitedInputViewType } from '../../stores/types/session';
import { SrtStreamStatus } from '../../stores/types/video';
import { LOCATION_PUBLISHING_ROUTES } from '../../stores/reducers/video';
import { setModalState } from '../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import EntryCode from './conferencing/entry-code';
import { PlatformButton } from './conferencing/elements';
import colors from '../../utils/colors';

export const Title = styled.h2`
	background: linear-gradient(104.42deg, ${colors.pinkLemonadePink} -4.67%, ${colors.lemon} 69.07%, ${colors.paleGreen} 120.95%);
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	margin-bottom: 16px;

	&.success {
		background: linear-gradient(91.27deg, #5AE4FF -2.35%, ${colors.paleGreen} 67.26%, ${colors.lemon} 107.71%) text;
	}
`;

const Subtitle = styled.div`
	color: white;

	&.error {
		color: #FF5F7F;
	}
`;

const SafetyImage = styled.img`
	width: 84px;
	height: 48px;
	margin-right: 16px;
	border-radius: 8px;
	object-fit: cover;
`;

const BackButton = styled.button`
	position: fixed;
	bottom: 60px;
	left: 60px;
	display: flex;
	align-items: center;
	gap: 8px;
	color: white;
	cursor: pointer;
	font-size: 13px;
	font-weight: 600;
	background: transparent;
	border: none;
	padding: 0;
	line-height: 20px;

	:hover, :active {
		background: transparent;
	}
`;

interface Props {
	exitConferenceRoomFlow: () => void;
}

enum SIPConnectionStatus {
	None = 'none',
	Connecting = 'connecting',
	Success = 'success',
	Error = 'error',
}

export enum Pages {
	Main = 0,
	ViewConfig = 1,
	SIPConfig = 2,
	SIPConnection = 3,
	Success = 4
}

export default function SetupConferenceRoom({ exitConferenceRoomFlow }: Props) {
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meetingId = useSelector((s: GS) => s.SessionReducer.meeting?.meeting_id);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const session = useSelector((s: GS) => s.SessionReducer.session);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const srtStreams = useSelector((s: GS) => s.VideoReducer.srtStreams);
	const {use_synergysky: useSynergyskyFeatureFlag} = useSelector((s: GS) => s.SettingsReducer.featureFlags);

	const [page, setPage] = useState<number>(Pages.Main);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [showAdvanced, setShowAdvanced] = useState(false);
	const [connectionName, setConnectionName] = useState('');
	const [SIPType, setSIPType] = useState('zoom');
	const [uploadedBackgroundImage, setUploadedBackgroundImage] = useState<File | null>(null);
	const [uploadImageURL, setUploadImageURL] = useState<string | null>(null);
	const [advancedUsername, setAdvancedUsername] = useState('');
	const [advancedPassword, setAdvancedPassword] = useState('');
	const [sipAddress, setSipAddress] = useState('');
	const [entryCode, setEntryCode] = useState<string>('');
	const [isConnected, setIsConnected] = useState(false);
	const [sipConnectionStatus, setSIPConnectionStatus] = useState<SIPConnectionStatus>(
		SIPConnectionStatus.None,
	);

	// conference room config
	const [view, setView] = useState<LimitedInputViewType>('none');
	const [locationId, setLocationId] = useState<number | null>(null);
	const [smartCanvas, setSmartCanvas] = useState(false);

	const dispatch = useDispatch();
	const { socket } = useContext(SocketContext);

	const otherExistingPublisher = useMemo(() => {
		if (locationId) {
			return connectedUsers.some((u) => (
				u.session !== session
				&& u.onsiteLocationSession
				&& u.onsiteLocationSession.id === locationId
				&& LOCATION_PUBLISHING_ROUTES.includes(u.onsiteLocationSession.route)
			));
		}
		return false;
	}, [connectedUsers, locationId, session]);

	const existingSrtStream = useMemo(() => {
		const selectedLocation = onsiteLocations.find((location) => location.id === locationId);
		if (selectedLocation) {
			return srtStreams.some(
				(s) => s.id === selectedLocation.srtId && s.status === SrtStreamStatus.active,
			);
		}
		return false;
	}, [srtStreams, locationId, onsiteLocations]);

	useEffect(() => {
		if (
			(page === Pages.Success || sipConnectionStatus === SIPConnectionStatus.Success)
			&& window.sessionStorage.getItem('goHomeOnReload') !== 'true'
		) {
			window.sessionStorage.setItem('goHomeOnReload', 'true');
		}
	}, [page, sipConnectionStatus]);

	const fileInput = useRef<HTMLInputElement | null>(null);

	const connectToSipUser = async () => {
		setPage(Pages.SIPConnection);
		setSIPConnectionStatus(SIPConnectionStatus.Connecting);

		let safetyImageUri;
		if (uploadedBackgroundImage) {
			const uploadResponse = await UploadImage(token, uploadedBackgroundImage);
			safetyImageUri = uploadResponse.url;
		}

		CallSipUser(token ?? '', sipAddress, meetingId ?? '', connectionName, safetyImageUri ?? DefaultSafetyImage).then((response) => {
			if (response && response.streamId) {
				setSIPConnectionStatus(SIPConnectionStatus.Success);
			} else {
				setSIPConnectionStatus(SIPConnectionStatus.Error);
			}
		}).catch((_error) => {
			console.error('Error connecting to SIP user:', _error);
			setSIPConnectionStatus(SIPConnectionStatus.Error);
		});
	};

	const getEstablishingConnectionActionButtons = (status: SIPConnectionStatus) => {
		if (status === SIPConnectionStatus.Error) {
			return (
				<div className='button-container'>
					<button onClick={() => setPage(Pages.SIPConfig)} className="success">
						<Icon name={ICONS.SETTINGS} color={COLORS.WHITE} size={16} />
						Back to Configuration
					</button>
					<button className="next background-green font-weight-heavy" onClick={() => connectToSipUser()}>
						<Icon name={ICONS.ARROW_LEFT_CURVED} color={COLORS.BLACK} size={16} />
						Try again
					</button>
				</div>
			);
		}

		return (
			<div className='button-container'>
				{status === SIPConnectionStatus.Success && (
					<button onClick={() => dispatch(setCompanionMode(true))} className="success">Companion Mode</button>
				)}
				<button onClick={() => setPage(Pages.SIPConfig)} className={classNames('success', { 'margin-left-medium': status === SIPConnectionStatus.Success })}>
					<Icon name={ICONS.SETTINGS} color={COLORS.WHITE} size={16} />
					Back to Configuration
				</button>
			</div>
		);
	};

	const sipConnectionSubtitle = useMemo(() => {
		switch (sipConnectionStatus) {
			case SIPConnectionStatus.Success:
				return 'You can close your browser window';
			case SIPConnectionStatus.Error:
				return 'Failed to establish connection';
			default:
				return 'Establishing connection...';
		}
	}, [sipConnectionStatus]);

	const sipConnectionPage = useMemo(() => (
			<>
				<Title className={classNames('bl-font-header', sipConnectionStatus === SIPConnectionStatus.Success && 'success')}>
					{ sipConnectionStatus === SIPConnectionStatus.Success ? 'Device connected successfully' : 'Configure a SIP connection' }
				</Title>
				<Subtitle className={classNames('bl-font-p2', sipConnectionStatus === SIPConnectionStatus.Error && 'error')}>
					{ sipConnectionSubtitle }
				</Subtitle>
				<div className='tab-container'>
					{getEstablishingConnectionActionButtons(sipConnectionStatus)}
				</div>
			</>
	), [sipConnectionStatus]);

	const sipConfigPage = (
		<>
			<div className="setup-conference-room-header">
				<h1>Configure SIP</h1>
			</div>

			<div className="name-container">
				<TextInput
					placeholder="Name"
					value={connectionName}
					onChange={(e) => (e.target.value.length > 120 ? null : setConnectionName(e.target.value))}
				/>
				<SelectInput
					mainIcon={<Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={14} color={COLORS.WHITE} />}
					iconIfOpen={<Icon name={ICONS.KEYBOARD_ARROW_UP} size={14} color={COLORS.WHITE} />}
					options={[
						{ icon: <img src={ZoomLogo} />, label: 'Zoom', value: 'zoom' },
						{ icon: <img src={MicrosoftTeamsLogo} />, label: 'Teams', value: 'teams' },
						{ icon: <img src={WebexLogo} />, label: 'Webex', value: 'webex' },
						{ icon: <Icon name={ICONS.ADD} size={14} color={COLORS.WHITE} />, label: 'Other', value: 'other' },
					]}
					selected={SIPType}
					small
					ignoreSpacing
					hideEmptyOption
					noMaxHeight
					showOptionIcon
					onChange={(value) => setSIPType(value)}
				/>
			</div>

			<div className="left-labeled-input-container">
				<span>SIP URI</span>
				<TextInput
					placeholder="Find this in your app's SIP configuration"
					value={sipAddress}
					onBlur={() => 'TODO'}
					labelStyle={{}}
					wrapperStyle={{}}
					onChange={(e) => setSipAddress(e.target.value)}
				/>
			</div>
			<div className="background-image-text">
				Safety Image
				<TooltipWrapper message="This will show while the camera is off" position={TTPosition.right}>
					<div className="info-i-tooltip">
						<Icon name={ICONS.INFO_CIRCLE} size={12} color={COLORS.TEXT_GRAY_400} />
					</div>
				</TooltipWrapper>
			</div>
			<div className="background-image-upload-container">
				<div className="background-image-upload-container-left">
					<SafetyImage src={uploadImageURL ?? DefaultSafetyImage} alt='Safety Image'/>
					<div className="image-upload-file-name">
						{uploadedBackgroundImage?.name
							? uploadedBackgroundImage.name
							: 'BL Conference room'}
					</div>
				</div>

				<div className="upload-buttons-container">
					<button className="replace-button" onClick={() => fileInput.current?.click()}>
						Replace
					</button>
					{uploadedBackgroundImage?.name
						? <button className="delete-button" onClick={() => {
							setUploadedBackgroundImage(null);
							setUploadImageURL(null);
							if (fileInput.current) fileInput.current.value = '';
						}}>
							<Icon name={ICONS.TRASH} color={COLORS.WHITE} size={12} />
						</button>
						: null}
				</div>

				<input
					accept={IMG_TYPES.map((a) => `.${a}`).join(',')}
					onChange={(e) => {
						const uploadFile = e.target.files?.[0];
						if (uploadFile) {
							setUploadedBackgroundImage(uploadFile);
							setUploadImageURL(URL.createObjectURL(uploadFile));
						}
					}}
					ref={(ref) => { fileInput.current = ref; }}
					type="file"
					style={{ display: 'none' }}
				/>
			</div>

			{
				showAdvanced ? <>
					<div className="left-labeled-input-container advanced username">
						<span>Username</span>
						<TextInput
							placeholder=""
							value={advancedUsername}
							onChange={(e) => setAdvancedUsername(e.target.value)}
						/>
					</div>
					<div className="left-labeled-input-container advanced password">
						<span>Password</span>
						<TextInput
							placeholder=""
							value={advancedPassword}
							onChange={(e) => setAdvancedPassword(e.target.value)}
							type="password"
						/>
					</div>
				</> : null
			}
			<div className='tab-container'>
				<div className='button-container'>
					<button className="back" onClick={() => setPage(Math.max(0, page - 1))}>Cancel</button>
					<button className="next background-green font-weight-heavy" onClick={() => connectToSipUser()}>
						Connect
					</button>
				</div>
			</div>
		</>
	);

	const handleViewConfigContinue = async () => {
		const config: LimitedInputConfig = {
			view,
			smartCanvas,
			locationId,
		};

		// applying config changes to an already connected user
		if (isConnected) {
			if (socket && user) {
				socket?.sendMessage(
					'limited-input-config-update',
					config,
					`gr_user-${user?.gr_user}`,
				);
			}
			setPage(Pages.Success);
			return;
		}

		// append the initial limited input config as query params to the redirect URL
		const url = new URL(window.location.href);
		url.searchParams.append('view', view);
		if (view === 'canvas') url.searchParams.append('smartCanvas', smartCanvas.toString());
		if (view === 'canvas' && locationId) url.searchParams.append('locationId', locationId.toString());

		if (await authorizeLimitedInput(entryCode, url.toString())) {
			setIsConnected(true);
			setPage(Pages.Success);
		} else {
			Alerts.showAlert({
				message: 'Join Error',
				description: 'There was an error with the code you entered. Please try again.',
				duration: 4000,
				type: 'error',
			});
			setPage(Pages.Main);
		}
	};

	const handleViewConfigContinueAttempt = () => {
		if ((otherExistingPublisher || existingSrtStream) && !isConnected) {
			dispatch(setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.MULTI_ONSITE_LOCATION,
				modalData: {
					socket,
					onsiteLocationId: locationId,
					session,
					handlePublish: handleViewConfigContinue,
				},
			}));
			return;
		}
		handleViewConfigContinue();
	};

	const getBackButtonDestination = (currentPage: number) => {
		switch (currentPage) {
			case Pages.ViewConfig:
				return Pages.Main;
			case Pages.SIPConfig:
				return Pages.Main;
			case Pages.Success:
				return Pages.ViewConfig;
			default:
				return null;
		}
	};

	const handleBackNavigation = () => {
		const destination = getBackButtonDestination(page);
		if (destination === null) {
			exitConferenceRoomFlow();
		} else {
			setPage(destination);
		}
	};

	return (
		<div className="onboarding-tabs onboarding-tabs-profile-update setup-conference-room" >
			<div className="tabs-container one">
				{(page === Pages.Main || page === Pages.Success) && (
					<div className='fixed-fill flex-center'>
						<img className="conference-room-background-image" src={SIPConferenceBackground} />
						<div className="conference-room-background-overlay-1" />
						<div className="conference-room-background-overlay-2" />
					</div>
				)}
				<div className="tab-wrapper one">
					{(page !== Pages.Main && page !== Pages.Success) && (
						<div className={classNames('gradient-container-container', { centered: page === Pages.ViewConfig })}>
							<div className='gradient-container'>
								<div className="gradient yellow" />
								<div className="gradient purple" />
							</div>
							<div className='gradient-offset'>{/* to offset the gradient to the left of center */}</div>
						</div>
					)}
					<div className="main-container">
					{page === Pages.Main && (
							 useSynergyskyFeatureFlag  ? (
								<>
								<ConferenceRoomEntry
									setPage={setPage}
									setSIPType={setSIPType}
									setEntryCode={setEntryCode}
									initialCode={entryCode}
								/>
								<div className="platform-buttons">
									<PlatformButton>
										<img alt="Zoom" src={ZoomLogo} />
										<span>Zoom Rooms</span>
									</PlatformButton>
									<PlatformButton >
										<img alt="Teams" src={MicrosoftTeamsLogo} />
										<span>Teams Rooms</span>
									</PlatformButton>
									<PlatformButton>
										<img alt="Cisco" src={WebexLogo} />
										<span>Cisco</span>
									</PlatformButton>
									<PlatformButton>
										<Icon name={ICONS.VIDEO} size={12} color={COLORS.WHITE} />
										<span>Google Meet</span>
									</PlatformButton>
									<PlatformButton>
										<Icon name={ICONS.COMPUTER} size={12} color={COLORS.WHITE} />
										<span>HP Poly</span>
									</PlatformButton>
									<PlatformButton>
										<Icon name={ICONS.ADD} size={12} color={COLORS.WHITE} />
										<span>More</span>
									</PlatformButton>
								</div>
								</>
							) : (
								<EntryCode
									setPage={setPage}
									setSIPType={setSIPType}
									setEntryCode={setEntryCode}
									initialCode={entryCode}
								/>
							)
						)}
						{page === Pages.ViewConfig && (
							<ConferenceViewConfig
								setPage={setPage}
								selectedView={view}
								setView={(newView: LimitedInputViewType) => setView(newView)}
								locationId={locationId}
								setLocationId={setLocationId}
								smartCanvas={smartCanvas}
								setSmartCanvas={setSmartCanvas}
								onContinue={handleViewConfigContinueAttempt}
								isConnected={isConnected}
							/>
						)}
						{page === Pages.SIPConfig && sipConfigPage}
						{page === Pages.SIPConnection && sipConnectionPage}
						{page === Pages.Success && <SuccessSplash />}
					</div>
					{!(page === Pages.ViewConfig && isConnected) && (
						<BackButton onClick={handleBackNavigation}>
							<Icon name={ICONS.ARROW_LEFT} size={12} color={COLORS.WHITE} />
							Back
						</BackButton>
					)}
					{page === Pages.Main && !useSynergyskyFeatureFlag  ? (<img className="brandlive-logo" alt="Brandlive" src={BrandliveLogo} />) : null}
					{page === Pages.Main && useSynergyskyFeatureFlag  ? (
						<div className="learn-more-link">
							<a href="https://support.brandlive.com/hc/en-us/articles/31259261433243-How-to-Join-Greenroom-via-Conference-Room" target="_blank">Learn More</a>
						</div>
					) : null}
					{page === Pages.SIPConfig && (
						<div className="setup-help-link">
							<a href="https://support.brandlive.com/hc/en-us/articles/31259261433243-How-to-Join-Greenroom-via-Conference-Room" target="_blank">How to setup</a>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
